'use client';

import { getApiErrorMessage, useApi } from '@repo/api';
import type { ApiErrorMessageEntry } from '@repo/api';
import { ApiErrors } from '@repo/features/error';
import { LocationPlaceValue, mapCompanyLocationToPlaceValue } from '@repo/features/location';
import { companyLocationDefaultValues, CompanyLocationForm, CompanyLocationFormValues, createCompanyLocationFormSchema, mapCompanyLocationToFormValues, normalizeCompanyLocationPayload } from '@repo/form/location';
import { useAppForm } from '@repo/form';
import { useMutation, useQueryClient } from '@repo/tanstack/query';
import { Spinner } from '@repo/ui/components';
import { useState } from 'react';
import type { CompanyLocation } from '@repo/schema/location';
type LoadLocationFormProps = {
  companyId: number;
  existingLocation?: CompanyLocation;
  initialSearchQuery?: string;
  initialValues?: Partial<CompanyLocationFormValues>;
  locationId?: number;
  mode: 'create' | 'edit';
  onCancel: () => void;
  onCreated?: (place: LocationPlaceValue) => void;
  onUpdated?: (place: LocationPlaceValue) => void;
  orgIdOverride?: number;
};
export const CreateLoadLocationForm = ({
  companyId,
  existingLocation,
  initialSearchQuery,
  initialValues,
  locationId,
  mode,
  onCancel,
  onCreated,
  onUpdated,
  orgIdOverride
}: LoadLocationFormProps) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<ApiErrorMessageEntry[] | null>(null);
  const createMutation = useMutation(api.companies.locations.create.mutationOptions({
    path: {
      id: companyId
    }
  }, {
    onError: error => {
      setErrorMessage(getApiErrorMessage(error));
    },
    onSuccess: async location => {
      setErrorMessage(null);
      await queryClient.invalidateQueries({
        exact: false,
        queryKey: ['companies', companyId, 'locations']
      });
      onCreated?.(mapCompanyLocationToPlaceValue(location));
    }
  }));
  const updateMutation = useMutation(api.companies.locations.update.mutationOptions({
    path: {
      companyId,
      id: locationId ?? 0
    }
  }, {
    // Load drops embed the company location, so edits reach load details and the table
    meta: {
      invalidates: [['loads']]
    },
    onError: error_0 => {
      setErrorMessage(getApiErrorMessage(error_0));
    },
    onSuccess: async location_0 => {
      setErrorMessage(null);
      await queryClient.invalidateQueries({
        exact: false,
        queryKey: ['companies', companyId, 'locations']
      });
      await queryClient.invalidateQueries({
        queryKey: api.companies.locations.get.queryKey({
          path: {
            companyId,
            id: location_0.id
          }
        })
      });
      onUpdated?.(mapCompanyLocationToPlaceValue(location_0));
    }
  }));
  const isPending = createMutation.isPending || updateMutation.isPending;
  const defaultValues: CompanyLocationFormValues = mode === 'edit' && existingLocation ? mapCompanyLocationToFormValues(existingLocation) : {
    ...companyLocationDefaultValues,
    ...initialValues,
    address: {
      ...companyLocationDefaultValues.address,
      ...initialValues?.address
    }
  };
  const form = useAppForm({
    defaultValues,
    onSubmit: async ({
      value
    }) => {
      setErrorMessage(null);
      try {
        const payload = normalizeCompanyLocationPayload(value);
        if (mode === 'edit' && locationId) {
          updateMutation.mutate(payload);
        } else {
          createMutation.mutate({
            ...payload,
            org_id: orgIdOverride
          });
        }
      } catch (error_1) {
        setErrorMessage(getApiErrorMessage(error_1));
      }
    },
    validators: {
      onSubmit: createCompanyLocationFormSchema()
    }
  });
  const errorContent = <ApiErrors errors={errorMessage} />;
  const isPrefilledCreate = mode === 'create' && !!initialValues && Boolean(initialValues.address?.address?.trim() || initialValues.address?.city?.trim() || initialValues.address?.country?.trim() || initialValues.address?.lat != null && String(initialValues.address.lat).trim() || initialValues.address?.lng != null && String(initialValues.address.lng).trim() || initialValues.address?.place_id?.trim() || initialValues.address?.place_name?.trim() || initialValues.address?.postcode?.trim());
  return <CompanyLocationForm cancelLabel="Cancel" disabled={isPending} errorContent={errorContent} form={form} mode={mode} onCancel={onCancel} prefilled={isPrefilledCreate} search={mode === 'create' ? {
    initialQuery: initialSearchQuery
  } : undefined} showActions submitIcon={isPending ? <Spinner className="size-4" /> : undefined} submitPending={isPending} data-sentry-element="CompanyLocationForm" data-sentry-component="CreateLoadLocationForm" data-sentry-source-file="form.tsx" />;
};